type OffrWindow = Window & {
  __OFFR_RENDER_READY__?: boolean;
};

const READY_TIMEOUT_MS = 4_000;

const nextFrame = () =>
  new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });

const waitForFonts = async () => {
  if (!("fonts" in document)) return;
  await document.fonts.ready;
};

const waitForImage = async (image: HTMLImageElement) => {
  if (!image.complete) {
    await new Promise<void>((resolve) => {
      const finish = () => resolve();
      image.addEventListener("load", finish, { once: true });
      image.addEventListener("error", finish, { once: true });
    });
  }

  if (typeof image.decode === "function") {
    await image.decode().catch(() => undefined);
  }
};

const settleCriticalAssets = async () => {
  const assets = Promise.allSettled([
    waitForFonts(),
    ...Array.from(document.images, waitForImage)
  ]);
  const timeout = new Promise<void>((resolve) => {
    window.setTimeout(resolve, READY_TIMEOUT_MS);
  });

  await Promise.race([assets, timeout]);
};

export function prepareCaptureMode() {
  const captureRequested = new URLSearchParams(window.location.search).get(
    "capture"
  );
  const automatedUserAgent = /HeadlessChrome|Playwright|Puppeteer/i.test(
    navigator.userAgent
  );
  const isAutomatedCapture =
    navigator.webdriver ||
    automatedUserAgent ||
    captureRequested === "1" ||
    captureRequested === "true";

  if (isAutomatedCapture) {
    document.documentElement.dataset.offrCapture = "true";
  }
}

export async function markRenderReady() {
  const root = document.getElementById("root");
  if (!root || root.dataset.hydrated === "true") return;

  root.dataset.hydrated = "true";
  await settleCriticalAssets();
  await nextFrame();
  await nextFrame();

  document.documentElement.dataset.renderReady = "true";
  (window as OffrWindow).__OFFR_RENDER_READY__ = true;
  window.dispatchEvent(new Event("offr:render-ready"));
}
